import * as R from 'ramda';
import { all, put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { openLoader, closeLoader } from '../../components/loader/actions';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { updateEntityFromFleetMapSuccess } from '../fleet-profile/actions';
import { geoFencingLocationReport } from '../geo-fencing-location/settings/location-settings';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature fleet-map
import { makeSelectFleets } from './selectors';
import {
  setFleetsToStore,
  cleanFleetMapStore,
  setFleetsLoadedToStore,
  openMapPageFromFleetList,
  setGeoFencingLocationToStore,
  getBranchConfigsByNamesSuccess,
} from './actions';
//////////////////////////////////////////////////

function* getBranchConfigsByNames() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
        names: R.join(',', [GC.FLEET_GENERAL_GEO_FENCING_LOCATION_TYPE]),
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const mapped = G.mapConfigValuesByName(data);

      yield put(getBranchConfigsByNamesSuccess(mapped));
    } else {
      yield call(G.handleFailResponse, res, 'getBranchConfigsByNames exception');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getBranchConfigsByNames exception');
  }
}

function* getFleetListSaga({ endpoint, fleetType }) {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { [GC.FIELD_BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpoint, options);

    const { status, data } = res;

    if (G.isResponseSuccess(status)) {
      yield put(setFleetsToStore({ fleetType, data }));
    } else {
      yield call(G.handleFailResponse, res, 'getFleetListSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getFleetListSaga exception');
  }
}

function* geoFencingLocationListRequestSaga() {
  try {
    yield put(openLoader({ showDimmer: true }));

    const currentEnterprise = yield select(makeSelectCurrentBranchGuid());

    const reqBody = {
      offset: 0,
      limit: 1000,
      currentEnterprise,
      fields: R.concat(
        G.getOrElse(geoFencingLocationReport, 'fields', []),
        [
          { sequence: 100, name: GC.FIELD_COORDS },
          { sequence: 101, name: GC.FIELD_SHAPE_TYPE },
        ],
      ),
    };

    const reqData = G.setSearchCriteria({ reqBody });

    const res = yield call(sendRequest, 'post', endpointsMap.geoFencingLocationList, { data: reqData });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(setGeoFencingLocationToStore(data));
    } else {
      yield call(G.handleFailResponse, res, 'geoFencingLocationListRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'geoFencingLocationListRequestSaga exception');
  }
}

function* handleOpenMapPageFromFleetListSaga({ payload }) {
  yield put(openLoader({ showDimmer: true }));

  yield put(cleanFleetMapStore());

  const { fleetsLoaded } = yield select(makeSelectFleets());

  if (R.includes(payload, [GC.FLEET_TRAILER_REPORT, GC.FLEET_TRAILER_ARCHIVED_REPORT])) {
    yield call(getBranchConfigsByNames);

    yield put(setFleetsLoadedToStore({
      ...fleetsLoaded,
      trucks: true,
      drivers: true,
    }));

    yield call(getFleetListSaga, {
      fleetType: 'trailers',
      endpoint: endpointsMap.trailerWithLastLocationListFleetMap,
    });

    yield call(geoFencingLocationListRequestSaga);
  }

  if (R.includes(payload, [GC.FLEET_TRUCK_REPORT, GC.FLEET_TRUCK_ARCHIVED_REPORT])) {
    yield put(setFleetsLoadedToStore({
      ...fleetsLoaded,
      drivers: true,
      trailers: true,
    }));

    yield call(getFleetListSaga, {
      fleetType: 'trucks',
      endpoint: endpointsMap.truckWithLastLocationList,
    });
  }

  yield put(closeLoader());
}

function* handleUpdateEntitySaga({ payload }) {
  const optionsMap = {
    driver: {
      fleetType: 'drivers',
      endpoint: endpointsMap.driverWithLastLocationList,
    },
    truck: {
      fleetType: 'trucks',
      endpoint: endpointsMap.truckWithLastLocationList,
    },
    trailer: {
      fleetType: 'trailers',
      endpoint: endpointsMap.trailerWithLastLocationListFleetMap,
    },
  };

  yield call(getFleetListSaga, optionsMap[R.or(payload, 'trailer')]);
}

function* handleVisitAnalyticMapPageSaga({ payload }) {
  yield call(visitPageSaga, payload, GC.CHECK_VISIT_ANALYTIC_MAPS_PAGE);

  yield put(openLoader({ showDimmer: true }));

  yield put(cleanFleetMapStore());

  yield call(getBranchConfigsByNames);

  yield all([
    call(getFleetListSaga, {
      fleetType: 'drivers',
      endpoint: endpointsMap.driverWithLastLocationList,
    }),
    call(getFleetListSaga, {
      fleetType: 'trucks',
      endpoint: endpointsMap.truckWithLastLocationList,
    }),
    call(getFleetListSaga, {
      fleetType: 'trailers',
      endpoint: endpointsMap.trailerWithLastLocationListFleetMap,
    }),
  ]);

  yield call(geoFencingLocationListRequestSaga);

  yield put(closeLoader());
}

function* fleetMapWatcherSaga() {
  yield takeLatest(updateEntityFromFleetMapSuccess, handleUpdateEntitySaga);
  yield takeLatest(GC.VISIT_ANALYTIC_MAPS_PAGE, handleVisitAnalyticMapPageSaga);
  yield takeLatest(openMapPageFromFleetList, handleOpenMapPageFromFleetListSaga);
}

export default fleetMapWatcherSaga;
